import React, {
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import uniqueId from 'lodash/uniqueId';
import {
  useGetAISessionInfo,
  useGetAISessionQueryContext,
} from 'pages/Dashboard/hooks/misc';
import isNil from 'lodash/isNil';
import Skeleton from 'core/components/Skeleton';
import clsx from 'clsx';
import Input from 'pages/Dashboard/components/Input';
import IconButton from '@mui/material/IconButton';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import {
  isEmptyString,
} from '@populateui/utils';
import {
  aiAssistantEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import usePopulateAIAssistant from 'views/EMR/components/AIAssistant/useAIAgent';
import Dialog from 'pages/Dashboard/components/Dialog';
import ToolTip from 'core/components/Tooltip';
import MicIcon from 'core/icons/mic';

export type Props = {
  onClose: () => void;
};

function getContext(chatMessageList: any[], query: string, queryContext: string) {
  return `
    - Your previous conversation with the user is ${JSON.stringify(chatMessageList)}.
    - Please reply to the question '${query}' keeping the following Context in mind, 
    - Context: '${queryContext}', only use the context if it is relevant to the question.
    - If you don't know the answer and not related to the context, 
    just say you don't have the information. Do not directly mention the context.
    - If the user asks for any list, add options as a list in the transcript.
    - Do not greet with name more than once.
  `;
}

type ChatProps = {
  type: 'user' | 'assistant';
  content: string | ReactNode;
};

function Chat({ content, type }: ChatProps) {
  return (
    <div className="flex gap-1 flex-col">
      <span className={clsx('flex gap-1 items-start max-w-[75%] font-semibold', {
        'text-gray-600': type === 'user',
        'text-blue-500': type === 'assistant',
      })}
      >
        <span className="text-sm text-inherit min-w-[48px]">
          {type === 'assistant' ? 'AI: ' : 'User: '}
        </span>
        <span className="text-sm flex whitespace-pre-wrap">
          {content}
        </span>
      </span>
    </div>
  );
}

export default function AIAssistantDialog({ onClose }: Props) {
  const [id, setId] = useState<string | null>(null);
  const [query, setQuery] = useState<string | null>('');

  useEffect(() => {
    setId(uniqueId('ai-session-'));
  }, []);

  const { data } = useGetAISessionInfo(id);

  const {
    data: queryContext,
    isLoading: queryContextLoading,
  } = useGetAISessionQueryContext(query ?? '', data?.id);

  const {
    startSession,
    stopSession,
    sendTextMessage,
    ready,
    chatMessageList,
    addContext,
    agentStatus,
    responseDelta,
  } = usePopulateAIAssistant();

  useEffect(() => {
    if (!isNil(queryContext)) {
      const context = getContext(chatMessageList, query ?? '', queryContext ?? '');
      addContext(context);
      setQuery('');
    }
  }, [queryContext]);

  const init = async () => {
    await startSession(data.client_secret.value);
  };

  useEffect(() => {
    if (!isNil(data)) {
      init();
    }
  }, [data]);

  useEffect(() => {
    const unsubscribe = aiAssistantEventChannel.on('onNewUserMessage', (message) => {
      setQuery((prevQuery) => prevQuery + message);
    });
    return () => {
      unsubscribe();
      stopSession();
    };
  }, []);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    const messagesEnd = messagesEndRef.current;
    setTimeout(() => {
      if (!isNil(messagesEnd)) {
        messagesEnd.scrollTop = messagesEnd.scrollHeight;
      }
    }, 0);
  };

  useEffect(() => {
    if (chatMessageList.length > 0 || !isEmptyString(responseDelta.text)) {
      scrollToBottom();
    }
  }, [chatMessageList, responseDelta]);

  const [instantMessage, setInstantMessage] = useState('');

  const handleClose = async () => {
    await stopSession();
    onClose();
  };

  return (
    <Dialog
      open
      className="w-[960px] h-[800px]"
      onClose={handleClose}
      title="Populate AI Assistant"
    >
      <div className="flex flex-col gap-2 h-full overflow-hidden">
        {ready ? (
          <>
            {chatMessageList.length === 0 ? (
              <div className="flex flex-col gap-2 items-center justify-center flex-1 w-full">
                <span className="text-sm font-semibold">Start talking</span>
                <span className="text-sm text-gray-400 text-center">E.g. Show me my upcoming appointments</span>
              </div>
            ) : (
              <div className="flex flex-col gap-2 text-xs overflow-y-auto flex-1 pr-4" ref={messagesEndRef}>
                {chatMessageList.map((message) => (
                  <Chat
                    key={message.id}
                    content={message.content}
                    type={message.type}
                  />
                ))}
                {(queryContextLoading || !isEmptyString(responseDelta.text)) && (
                  <Chat
                    type="assistant"
                    content={!isEmptyString(responseDelta.text)
                      ? responseDelta.text
                      : (<Skeleton className="!h-[24px] !w-96 !bg-blue-200" />)}
                  />
                )}
              </div>
            )}
            <div className="flex gap-2 items-center py-2">
              <span className="text-xs text-gray-700 font-semibold">
                <ToolTip title={agentStatus.userTalking ? 'Listening...' : 'Start asking questions'}>
                  <div>
                    <MicIcon
                      className={clsx('text-gray-light !h-4 !w-4', {
                        'text-red-600': agentStatus.userTalking,
                      })}
                    />
                  </div>
                </ToolTip>
              </span>
              <Input
                className="flex-1"
                value={instantMessage}
                placeholder="Or type your question"
                onChange={(value) => setInstantMessage(value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    sendTextMessage(instantMessage);
                    setQuery(instantMessage);
                    setInstantMessage('');
                  }
                }}
              />
              <IconButton
                onClick={() => {
                  sendTextMessage(instantMessage);
                  setQuery(instantMessage);
                  setInstantMessage('');
                }}
                disabled={isEmptyString(instantMessage)}
              >
                <SendOutlinedIcon className="!text-blue-500" />
              </IconButton>
            </div>
          </>
        ) : (<Skeleton className="!w-full" />)}

      </div>
    </Dialog>
  );
}
